/**
 * HeroSafetyFrame Web Component
 * Migrated from src/alpine/components/hero-safety-frame-and-lenses.js
 *
 * Custom element <hero-safety-frame> manages the product page:
 * - Frame & lens product selection
 * - Image galleries with slider
 * - Variant selection, price calculation
 * - Cart operations (frame-only & prescription)
 * - Prescription popup delegation to <rx-form-safety>
 */
(function () {
  "use strict";

  var MO = window.MO;
  var TEMPLE_IFRAME_URL = "https://temple.services.xprizm.com";

  class HeroSafetyFrame extends HTMLElement {
    constructor() {
      super();

      // ─── State ──────────────────────────────────────────────
      this.product = null; // frame product
      this.selectedVariant = null;
      this.productOptions = null;

      this.rxLensProducts = window.RxLensProducts || [];
      this.rxLensProduct = this.rxLensProducts[0]
        ? this.rxLensProducts[0].product
        : null;
      this.rxLensSelectedVariant = this.rxLensProduct
        ? this.rxLensProduct.selectedVariant
        : null;
      this.rxLensProductOptions = this.rxLensProduct
        ? this.rxLensProduct.productOptions
        : null;

      this.lensCoatingProduct = null;
      this.lensCoatingSelectedVariant = null;
      this.noLensVariantId = null;
      this.isAddedCoatingProduct = true;

      this.temple1 = null;
      this.temple2 = null;
      this.measurementId = null;
      this.handler = null;

      this.shopProducts = {};
      this.framesByGroup = [];
      this.activeFrameGroup = null;

      this.images = [];
      this.prescriptionItems = [];

      this.orderType = "Prescription";
      this.isOpenPrescriptionPopup = false;
      this.isOpenFramePopup = false;
      this.activeEyewear = "";
      this.customSize = "";
      this.activeTags = { category: null, frame: null, eyewear: null };

      this.sectionId = "";
      this.qty = 1;

      // Step confirmation tracking
      this._confirmedSteps = new Set();

      // Sliders
      this._mobileSlider = null;
      this._externalSlider = null;

      // Bound handlers
      this._scrollHandler = this._onScroll.bind(this);
      this._rxToggleHandler = this._onRxTogglePopup.bind(this);
    }

    // ── Lifecycle ──────────────────────────────────────────────
    connectedCallback() {
      this.sectionId = this.getAttribute("data-section-id") || "";

      // Parse lens coating product from embedded script tag
      var coatingScript = this.querySelector('[data-coating-product]');
      if (coatingScript) {
        try {
          this.lensCoatingProduct = JSON.parse(coatingScript.textContent);
          this.lensCoatingSelectedVariant =
            this.lensCoatingProduct.variants[0] || null;
        } catch (e) {
          console.error("Failed to parse coating product:", e);
        }
      }

      var nlv = this.getAttribute("data-no-lens-variant-id");
      if (nlv) this.noLensVariantId = parseInt(nlv, 10);

      this._init();
    }

    disconnectedCallback() {
      window.removeEventListener("scroll", this._scrollHandler);
      this.removeEventListener("rx-toggle-popup", this._rxToggleHandler);
      if (this._mobileSlider) this._mobileSlider.destroy();
      if (this._externalSlider) this._externalSlider.destroy();
    }

    // ── Initialisation ────────────────────────────────────────
    async _init() {
      // Parse product JSON from embedded script tag (ev-product-alpine-json)
      var jsonEl = this.querySelector('[data-product-json]');
      if (!jsonEl) return;

      var data;
      try {
        data = JSON.parse(jsonEl.textContent);
      } catch (e) {
        console.error("Failed to parse product JSON:", e);
        return;
      }

      var product = data.product;
      var productOptions = data.productOptions;
      var selectedVariant =
        data.selectedVariant || this._firstAvailableVariant(product);

      // Set defaults
      this._setDefaultActiveTags(product.tags);
      if (this.rxLensProduct) {
        this._setDefaultActiveTags(this.rxLensProduct.tags);
      }
      this._setInstance({ product, productOptions, selectedVariant });
      this._setProductImagesByAlt();
      this._setCssVariables();

      // Bind events
      this._bindEvents();
      this._initSteps();
      this._portalStickyBar();
      window.addEventListener("scroll", this._scrollHandler, {
        passive: true,
      });
      this.addEventListener("rx-toggle-popup", this._rxToggleHandler);

      // Initial render
      this.render();
      this._initSliders();

      // Init eyewear from first RxLensProduct
      if (this.rxLensProducts[0]) {
        this.activeEyewear = this.rxLensProducts[0].label || "";
      }

      // Fetch all frames data (async)
      try {
        var shopProducts = await this._getDataFromPage(
          "/pages/maskoptix-diving-glasses-about-us?view=ev-rx-products-json"
        );
        this.shopProducts = shopProducts;
        this._buildFramesByGroup(shopProducts);
        this.render();
      } catch (e) {
        console.error("Failed to fetch shop products:", e);
      }
    }

    // ── Render ─────────────────────────────────────────────────
    render() {
      var self = this;

      // Visibility
      // Count available frame colors for current group
      var frameGroup = this._getFrameGroup();
      var groupEntry = this.framesByGroup.find(function (g) {
        return g.frameGroup === frameGroup;
      });
      var frameColorCount = 0;
      if (groupEntry) {
        frameColorCount = groupEntry.allFrames.filter(function (p) {
          if (typeof p.available === "boolean") return p.available;
          if (p.variants && p.variants.length) {
            return p.variants.some(function (v) { return v.available; });
          }
          return true;
        }).length;
      }

      var hasMultipleColors = frameColorCount > 1;
      var frameNotDefault = !!(this.product && !this.product.has_only_default_variant);

      var vis = {
        prescriptionPopup: this.isOpenPrescriptionPopup,
        orderTypePrescription: this.orderType === "Prescription",
        frameNotDefault: frameNotDefault,
        multipleFrameColors: hasMultipleColors,
        hasFrameOptions: hasMultipleColors || frameNotDefault,
        descriptionExists: !!(this.product && this.product.description),
        externalGallery: !!(
          this.product &&
          this.product.metafields &&
          this.product.metafields.external_gallery
        ),
        stickyCart:
          !this.isOpenPrescriptionPopup && !this.isOpenFramePopup,
        colorSwatchDesc: !!(
          this._getFrameTag() && window.colorSwatch && window.colorSwatch[this._getFrameTag()]
        ),
      };

      var applyShow = function (el) {
        var key = el.getAttribute("data-show");
        if (key in vis) {
          el.style.display = vis[key] ? "" : "none";
        }
      };
      this.querySelectorAll("[data-show]").forEach(applyShow);
      if (this._stickyBar) {
        applyShow(this._stickyBar);
        this._stickyBar.querySelectorAll("[data-show]").forEach(applyShow);
      }

      // Text
      this._text("frameGroup", this._getFrameGroup());
      this._text("frameColor", this._getFrameColor());

      // Price
      var priceEl = this._ref("currentPrice");
      if (priceEl) {
        priceEl.textContent = window.currency
          ? window.currency(this._getPrice())
          : this._getPrice();
      }

      // Compare at price
      var cap = this._getCompareAtPrice();
      var capEl = this._ref("compareAtPrice");
      if (capEl) {
        if (cap) {
          capEl.style.display = "";
          capEl.textContent = window.currency
            ? window.currency(cap.price)
            : cap.price;
        } else {
          capEl.style.display = "none";
        }
      }

      // Add-to-cart button states
      this.querySelectorAll("[data-ref='addToCartBtn']").forEach(function (btn) {
        var available = self.selectedVariant && self.selectedVariant.available;
        btn.disabled = !available;
        var span = btn.querySelector("[data-ref='addToCartText']");
        if (span) {
          span.textContent = available ? "Add to cart" : "Sold out";
        }
      });

      // Sticky cart frame name
      this._text("stickyFrameGroup", this._getFrameGroup());
      var stickyPrice = this._ref("stickyPrice");
      if (stickyPrice) {
        stickyPrice.textContent = window.currency
          ? window.currency(this._getPrice())
          : this._getPrice();
      }
      var stickyCapEl = this._ref("stickyCompareAtPrice");
      if (stickyCapEl) {
        if (cap) {
          stickyCapEl.style.display = "";
          stickyCapEl.textContent = window.currency
            ? window.currency(cap.price)
            : cap.price;
        } else {
          stickyCapEl.style.display = "none";
        }
      }

      this._renderStickyProgress();

      // Description
      var descMobile = this._ref("descMobilePreview");
      var descMobileFull = this._ref("descMobileFull");
      var descDesktop = this._ref("descDesktop");
      var desc = this.product ? this.product.description : "";
      if (descMobile) descMobile.innerHTML = desc || "";
      if (descMobileFull) descMobileFull.innerHTML = desc || "";
      if (descDesktop) descDesktop.innerHTML = desc || "";

      // Color swatch description
      var csDesc = this._ref("colorSwatchDescription");
      if (csDesc && window.colorSwatch) {
        var ft = this._getFrameTag();
        csDesc.innerHTML =
          ft && window.colorSwatch[ft]
            ? window.colorSwatch[ft].description || ""
            : "";
      }

      // Body overflow for prescription popup
      document.body.style.overflow = this.isOpenPrescriptionPopup
        ? "hidden"
        : "";

      // Render dynamic sections
      this._renderImages();
      this._renderDesktopImages();
      this._renderExternalGallery();
      this._renderVariantButtons();
      this._renderFrameColors();
      this._renderSwatches();
      this._renderLensOptions();
      this._renderCoatingVariants();
      this._renderEyewearTabs();
      this._renderOrderTypeButtons();

      // Update step states & summary
      this._renderStepStates();

      // Push context to rx-form WC
      this._pushRxFormContext();
    }

    // ── Dynamic section renderers ─────────────────────────────

    _renderImages() {
      var container = this._ref("mobileSliderTrack");
      if (!container) return;

      var html = this.images
        .map(function (img) {
          return (
            '<div class="mo-slider__slide tw-rounded-ra-8 tw-overflow-hidden">' +
            '<img class="tw-w-full tw-block tw-object-contain" ' +
            'src="' + (img.src || "") + '" ' +
            'alt="' + (img.alt || "").replace(/"/g, "&quot;") + '">' +
            "</div>"
          );
        })
        .join("");

      if (container.innerHTML !== html) {
        container.innerHTML = html;
        // Pagination width
        var pagination = this._ref("mobileSliderPagination");
        if (pagination) {
          pagination.style.setProperty(
            "--width",
            "calc(100% / " + this.images.length + ")"
          );
        }
      }
    }

    _renderDesktopImages() {
      var layout = this.getAttribute("data-gallery-layout") || "grid";

      if (layout === "single") {
        this._renderDesktopSingleImage();
      } else {
        this._renderDesktopGrid();
      }
    }

    _renderDesktopGrid() {
      var container = this._ref("desktopImagesGrid");
      if (!container) return;

      var html = this.images
        .map(function (img, index) {
          var colSpan = index === 0 ? " tw-col-span-2" : "";
          return (
            '<div class="tw-rounded-ra-8 tw-overflow-hidden' + colSpan + '">' +
            '<img class="tw-w-full tw-h-full tw-block tw-object-cover" ' +
            'src="' + (img.src || "") + '" ' +
            'alt="' + (img.alt || "").replace(/"/g, "&quot;") + '">' +
            "</div>"
          );
        })
        .join("");

      if (container.innerHTML !== html) {
        container.innerHTML = html;
      }
    }

    _renderDesktopSingleImage() {
      var mainContainer = this._ref("desktopMainImage");
      var thumbsContainer = this._ref("desktopThumbs");
      if (!mainContainer || !thumbsContainer) return;

      var self = this;
      var activeIndex = this._galleryActiveIndex || 0;
      if (activeIndex >= this.images.length) activeIndex = 0;
      var activeImg = this.images[activeIndex];

      // Main image
      var mainHtml =
        '<img class="tw-w-full tw-block tw-object-contain" ' +
        'src="' + (activeImg ? activeImg.src || "" : "") + '" ' +
        'alt="' + (activeImg ? (activeImg.alt || "").replace(/"/g, "&quot;") : "") + '">';

      if (mainContainer.innerHTML !== mainHtml) {
        mainContainer.innerHTML = mainHtml;
      }

      // Thumbnails
      var thumbsHtml = this.images
        .map(function (img, index) {
          var isActive = index === activeIndex ? " pdp-gallery-thumb--active" : "";
          return (
            '<button type="button" class="pdp-gallery-thumb' + isActive + '" data-thumb-index="' + index + '">' +
            '<img class="tw-w-full tw-h-full tw-block tw-object-cover" ' +
            'src="' + (img.src || "") + '" ' +
            'alt="' + (img.alt || "").replace(/"/g, "&quot;") + '">' +
            "</button>"
          );
        })
        .join("");

      if (thumbsContainer.innerHTML !== thumbsHtml) {
        thumbsContainer.innerHTML = thumbsHtml;
        thumbsContainer.querySelectorAll("[data-thumb-index]").forEach(function (btn) {
          btn.addEventListener("click", function () {
            self._galleryActiveIndex = parseInt(btn.getAttribute("data-thumb-index"), 10);
            self._renderDesktopSingleImage();
          });
        });
      }
    }

    _renderExternalGallery() {
      var container = this._ref("externalGalleryTrack");
      if (!container) return;

      var gallery =
        this.product &&
        this.product.metafields &&
        this.product.metafields.external_gallery;
      if (!gallery || !Array.isArray(gallery)) {
        container.innerHTML = "";
        return;
      }

      var html = gallery
        .map(function (imgUrl) {
          return (
            '<div class="mo-slider__slide tw-rounded-ra-8 tw-overflow-hidden">' +
            '<img class="tw-w-full tw-h-full tw-block tw-object-cover" src="' +
            imgUrl +
            '">' +
            "</div>"
          );
        })
        .join("");

      if (container.innerHTML !== html) {
        container.innerHTML = html;
      }
    }

    _renderVariantButtons() {
      var container = this._ref("variantButtonsGrid");
      if (!container || !this.product) return;

      var self = this;
      var html = this.product.variants
        .filter(function (v) { return v.available; })
        .map(function (v) {
          var isActive =
            self.selectedVariant && v.id === self.selectedVariant.id;
          var title = v.title.replace("(", "<br> (");
          return (
            '<button type="button" class="tag-button' +
            (isActive ? " active" : "") +
            '" data-variant-id="' + v.id + '">' +
            "<span>" + title + "</span>" +
            "</button>"
          );
        })
        .join("");

      container.innerHTML = html;

      // Bind clicks
      container.querySelectorAll("[data-variant-id]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var vid = parseInt(btn.getAttribute("data-variant-id"), 10);
          var variant = self.product.variants.find(function (v) {
            return v.id === vid;
          });
          if (variant) {
            self.selectedVariant = variant;
            self.render();
          }
        });
      });
    }

    _renderFrameColors() {
      var container = this._ref("frameColorSwatches");
      if (!container) return;

      var group = this._getFrameGroup();
      var frameGroup = this.framesByGroup.find(function (g) {
        return g.frameGroup === group;
      });
      var allFrames = frameGroup ? frameGroup.allFrames : [];

      // Filter out unavailable products (all variants out of stock)
      var availableFrames = allFrames.filter(function (p) {
        // If product-level "available" exists, use it
        if (typeof p.available === "boolean") return p.available;
        // Fallback: check if any variant is available
        if (p.variants && p.variants.length) {
          return p.variants.some(function (v) { return v.available; });
        }
        return true;
      });

      var self = this;
      var currentTag = this._getFrameTag();

      var html = availableFrames
        .map(function (p) {
          var pTag = self._getFrameTagByProduct(p);
          var pColor = self._getFrameColorByProduct(p);
          var isActive = currentTag === pTag;
          return (
            '<button type="button" class="ev-color-swatch' +
            (isActive ? " active" : "") +
            '" data-frame-handle="' + p.handle + '">' +
            '<span class="tw-sr-only">Change frame color</span>' +
            '<div data-color-swatch="' + (pColor || "") + '" class="ev-swatch"></div>' +
            "</button>"
          );
        })
        .join("");

      container.innerHTML = html;

      container.querySelectorAll("[data-frame-handle]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var handle = btn.getAttribute("data-frame-handle");
          var p = availableFrames.find(function (f) { return f.handle === handle; });
          if (p) {
            self._changeProductByProductType({
              productType: "frame_product",
              product: p,
            });
          }
        });
      });
    }

    _renderSwatches() {
      var container = this._ref("swatchesContainer");
      if (!container) return;

      if (
        this.orderType !== "Prescription" ||
        !this.rxLensProduct ||
        !this.rxLensProduct.productOptions
      ) {
        container.innerHTML = "";
        container.style.display = "none";
        return;
      }

      // Get the swatches matcher from data attribute
      var matcher = container.getAttribute("data-swatches-matcher") || "";
      var matchingOptions = this.rxLensProduct.productOptions.filter(
        function (o) {
          return matcher.includes(o.name);
        }
      );

      if (matchingOptions.length === 0) {
        container.innerHTML = "";
        container.style.display = "none";
        return;
      }

      container.style.display = "";
      var self = this;
      var mbD = container.getAttribute("data-mb-d") || "0";
      var mbM = container.getAttribute("data-mb-m") || "0";
      var label = container.getAttribute("data-label") || "";

      var html = matchingOptions
        .map(function (option) {
          var selectedVal = self.rxLensSelectedVariant
            ? self.rxLensSelectedVariant.options[option.position - 1]
            : "";
          var meta =
            self.rxLensProduct.metafields &&
            self.rxLensProduct.metafields.options_extension
              ? self.rxLensProduct.metafields.options_extension[option.name]
              : null;

          var valuesHtml = option.values
            .map(function (value) {
              var isActive = value === selectedVal;
              var swatchImg = "";
              if (meta && meta[value] && meta[value].swatch_url) {
                swatchImg =
                  '<img src="' + meta[value].swatch_url + '" alt="Color ' + value + '">';
              }
              return (
                '<button type="button" class="ev-color-swatch square' +
                (isActive ? " active" : "") +
                '" data-swatch-option="' + option.name + '" data-swatch-value="' + value + '">' +
                '<span class="tw-sr-only">Change lens color</span>' +
                swatchImg +
                "</button>"
              );
            })
            .join("");

          var descHtml = "";
          if (meta && meta[selectedVal] && meta[selectedVal].description) {
            descHtml =
              '<div class="tw-rounded-ra-8 tw-text-sc-text-weak txt-tiny">' +
              meta[selectedVal].description +
              "</div>";
          }

          return (
            '<div style="--mb-d: ' + mbD + "; --mb-m: " + mbM + ';" class="tw-mb-[var(--mb-m)] lg:tw-mb-[var(--mb-d)] tw-flex tw-flex-col tw-gap-2">' +
            '<div class="tw-flex tw-items-center tw-gap-1">' +
            '<span class="txt-uppercase tw-font-bold tw-text-sc-text-strong">' + label + "</span>" +
            '<span class="txt-tiny tw-text-sc-text-strong">' + (selectedVal || "") + "</span>" +
            "</div>" +
            '<div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2.5">' +
            valuesHtml +
            "</div>" +
            descHtml +
            "</div>"
          );
        })
        .join("");

      container.innerHTML = html;

      // Bind clicks
      container
        .querySelectorAll("[data-swatch-option]")
        .forEach(function (btn) {
          btn.addEventListener("click", function () {
            var optName = btn.getAttribute("data-swatch-option");
            var optVal = btn.getAttribute("data-swatch-value");
            self._changeVariant({ name: optName }, optVal);
          });
        });
    }

    _renderLensOptions() {
      var container = this._ref("lensOptionsContainer");
      if (!container) return;

      if (
        this.orderType !== "Prescription" ||
        !this.rxLensProduct ||
        !this.rxLensProduct.productOptions
      ) {
        // Don't clear — just hide via data-show
        return;
      }

      var self = this;
      var options = this.rxLensProduct.productOptions.filter(function (o) {
        return o.name !== "Color";
      });

      var html = options
        .map(function (option) {
          var selectedVal = self.rxLensSelectedVariant
            ? self.rxLensSelectedVariant.options[option.position - 1]
            : "";
          var meta =
            self.rxLensProduct.metafields &&
            self.rxLensProduct.metafields.options_extension
              ? self.rxLensProduct.metafields.options_extension[option.name]
              : null;

          var gridClass =
            option.name === "Type"
              ? "tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-2 lg:tw-gap-1"
              : option.name === "Material"
                ? "tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-1"
                : "tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-1";

          var buttonsHtml = option.values
            .map(function (value) {
              var isActive = selectedVal === value;
              var plusPrice = "";
              if (
                (option.name === "Type" || option.name === "Material") &&
                meta &&
                meta[value] &&
                meta[value].plus_price !== 0
              ) {
                var priceStr = window.currency
                  ? window.currency(meta[value].plus_price, true)
                  : meta[value].plus_price;
                plusPrice = " <br> +" + priceStr;
              }
              return (
                '<button type="button" class="tag-button' +
                (isActive ? " active" : "") +
                '" data-lens-option="' + option.name + '" data-lens-value="' + value + '">' +
                "<span>" + value + plusPrice + "</span>" +
                "</button>"
              );
            })
            .join("");

          var descHtml = "";
          if (meta && meta[selectedVal] && meta[selectedVal].description) {
            descHtml =
              '<div class="tw-rounded-ra-8 tw-text-sc-text-weak txt-tiny">' +
              meta[selectedVal].description +
              "</div>";
          }

          return (
            '<div class="tw-flex tw-flex-col tw-gap-2">' +
            '<span class="txt-uppercase tw-font-bold tw-text-sc-text-strong">' +
            option.name +
            "</span>" +
            '<div class="tw-grid ' + gridClass + '">' +
            buttonsHtml +
            "</div>" +
            descHtml +
            "</div>"
          );
        })
        .join("");

      container.innerHTML = html;

      // Bind clicks
      container
        .querySelectorAll("[data-lens-option]")
        .forEach(function (btn) {
          btn.addEventListener("click", function () {
            var optName = btn.getAttribute("data-lens-option");
            var optVal = btn.getAttribute("data-lens-value");
            self._changeVariant({ name: optName }, optVal);
          });
        });
    }

    _renderCoatingVariants() {
      var container = this._ref("coatingVariantsContainer");
      if (!container || !this.lensCoatingProduct) return;

      var self = this;
      var html = this.lensCoatingProduct.variants
        .map(function (v) {
          var isActive =
            self.lensCoatingSelectedVariant &&
            self.lensCoatingSelectedVariant.id === v.id;
          var suffix = "";
          var titleLower = v.title.toLowerCase();
          if (titleLower.indexOf("hard coat") !== -1) {
            suffix = ' <span style="color:#005696;font-weight:700">(Included)</span>';
          } else if (v.price !== 0) {
            var formatted = window.currency
              ? window.currency(v.price).trim()
              : v.price;
            var rec = (titleLower.indexOf("anti fog") !== -1 || titleLower.indexOf("anti-fog") !== -1) ? " (Recommended)" : "";
            suffix = ' <span style="color:#005696;font-weight:700">(+' + formatted + ')' + rec + '</span>';
          }
          return (
            '<div role="button" class="pdp-radio-option' + (isActive ? " is-selected" : "") + '" data-coating-id="' +
            v.id + '">' +
            '<span class="pdp-radio-dot"></span>' +
            '<span class="txt-small tw-text-sc-text-strong">' +
            v.title + suffix +
            "</span>" +
            "</div>"
          );
        })
        .join("");

      container.innerHTML = html;

      container
        .querySelectorAll("[data-coating-id]")
        .forEach(function (el) {
          el.addEventListener("click", function () {
            var cid = parseInt(el.getAttribute("data-coating-id"), 10);
            var variant = self.lensCoatingProduct.variants.find(function (v) {
              return v.id === cid;
            });
            if (variant) {
              self.lensCoatingSelectedVariant = variant;
              self._evaluateEyewearSwap();
              self.render();
            }
          });
        });
    }

    // Resolve which lens product to use for an eyewear button:
    // returns the swap product if the active coating matches the rule, else base.
    _resolveEyewearProduct(btn) {
      if (!btn) return null;
      var match = (btn.getAttribute("data-eyewear-swap-coating-match") || "").trim().toLowerCase();
      var coatingTitle = (this.lensCoatingSelectedVariant && this.lensCoatingSelectedVariant.title || "").toLowerCase();
      var swapEl = btn.querySelector("[data-eyewear-swap-product-json]");
      var baseEl = btn.querySelector("[data-eyewear-product-json]");

      if (match && swapEl && coatingTitle && coatingTitle.indexOf(match) !== -1) {
        try { return JSON.parse(swapEl.textContent); } catch (e) {
          console.error("Failed to parse swap eyewear product:", e);
        }
      }
      if (baseEl) {
        try { return JSON.parse(baseEl.textContent); } catch (e) {
          console.error("Failed to parse eyewear product:", e);
        }
      }
      return null;
    }

    // Re-evaluate active eyewear swap when coating changes.
    _evaluateEyewearSwap() {
      var activeBtn = this.querySelector("[data-eyewear-btn].active");
      if (!activeBtn) return;
      var resolved = this._resolveEyewearProduct(activeBtn);
      if (!resolved) return;
      if (this.rxLensProduct && String(this.rxLensProduct.id) === String(resolved.id)) return;
      this._changeProductByProductType({
        productType: "lens_product",
        product: resolved,
      });
    }

    _renderEyewearTabs() {
      var container = this._ref("eyewearTabsContainer");
      if (!container) return;

      var self = this;
      container.querySelectorAll("[data-eyewear-btn]").forEach(function (btn) {
        var pid = btn.getAttribute("data-eyewear-product-id");
        var swapId = btn.getAttribute("data-eyewear-swap-product-id");
        var currentId = self.rxLensProduct ? String(self.rxLensProduct.id) : "";
        var isActive =
          currentId !== "" &&
          (currentId === String(pid) || (swapId && currentId === String(swapId)));
        btn.classList.toggle("active", isActive);
      });
    }

    _renderOrderTypeButtons() {
      var self = this;
      this.querySelectorAll("[data-order-type-btn]").forEach(function (btn) {
        var val = btn.getAttribute("data-order-type-btn");
        btn.classList.toggle("active", self.orderType === val);
      });
    }

    // ── Helpers ────────────────────────────────────────────────
    _ref(name) {
      return (
        this.querySelector('[data-ref="' + name + '"]') ||
        document.querySelector('[data-ref="' + name + '"]')
      );
    }

    _portalStickyBar() {
      var bar = this.querySelector(".pdp-sticky-bar");
      if (!bar) return;
      if (bar.parentElement !== document.body) {
        document.body.appendChild(bar);
      }
      this._stickyBar = bar;
      this._liftChatWidget();
    }

    _liftChatWidget() {
      // Host element is Shopify Inbox's <inbox-online-store-chat> in light DOM.
      // CSS rule in wc-steps.css handles mobile offset; JS here only guards against
      // the widget re-setting inline `bottom: 16px` after our CSS evaluates.
      var OFFSET = 130;
      var MOBILE_MQ = window.matchMedia("(max-width: 1023px)");

      var apply = function () {
        var host = document.querySelector("inbox-online-store-chat");
        if (!host) return;
        if (MOBILE_MQ.matches) {
          host.style.setProperty("bottom", OFFSET + "px", "important");
        } else {
          host.style.removeProperty("bottom");
        }
      };

      apply();

      if (MOBILE_MQ.addEventListener) {
        MOBILE_MQ.addEventListener("change", apply);
      }

      if ("MutationObserver" in window) {
        var obs = new MutationObserver(apply);
        obs.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["style"],
        });
      }
    }

    _text(refName, value) {
      var el = this._ref(refName);
      if (el) el.textContent = value != null ? value : "";
    }

    // ── Event binding ─────────────────────────────────────────
    _bindEvents() {
      var self = this;

      // data-action handlers
      this.querySelectorAll("[data-action]").forEach(function (el) {
        var parts = el.getAttribute("data-action").split(":");
        var eventType = parts[0];
        var method = parts[1];

        el.addEventListener(eventType, function (e) {
          if (typeof self[method] === "function") {
            self[method](e, el);
          }
        });
      });

      // Eyewear tab buttons (rendered by Liquid, static)
      this.querySelectorAll("[data-eyewear-btn]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var label = btn.getAttribute("data-eyewear-label") || "";
          var product = self._resolveEyewearProduct(btn);
          if (!product) return;
          self._changeProductByProductType({
            productType: "lens_product",
            product: product,
          });
          self.changeActiveEyewear(label);
        });
      });

      // Order type buttons
      this.querySelectorAll("[data-order-type-btn]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var val = btn.getAttribute("data-order-type-btn");
          if (val) {
            self.orderType = val;
            self.render();
          }
        });
      });

      // Mask size input
      var maskInput = this.querySelector("[data-mask-size-input]");
      if (maskInput) {
        maskInput.addEventListener("input", function () {
          self.customSize = maskInput.value || "";
        });
      }

      // Description toggle (mobile)
      var descToggle = this._ref("descToggleBtn");
      if (descToggle) {
        descToggle.addEventListener("click", function () {
          var preview = self._ref("descMobilePreviewWrap");
          var full = self._ref("descMobileFullWrap");
          if (!preview || !full) return;

          var isOpen = full.style.display !== "none";
          preview.style.display = isOpen ? "" : "none";
          full.style.display = isOpen ? "none" : "";
          descToggle.textContent = isOpen ? "Read more" : "Show less";
        });
      }

      // Fit guide modal
      var fitOpen = this._ref("fitGuideOpenBtn");
      var fitModal = this._ref("fitGuideModal");
      if (fitOpen && fitModal) {
        fitOpen.addEventListener("click", function () {
          fitModal.style.display = "";
          fitModal.classList.add("tw-opacity-100");
        });

        fitModal.addEventListener("click", function (e) {
          if (e.target === fitModal) {
            fitModal.style.display = "none";
          }
        });

        var fitClose = fitModal.querySelector("[data-action-close-fit-guide]");
        if (fitClose) {
          fitClose.addEventListener("click", function () {
            fitModal.style.display = "none";
          });
        }

        document.addEventListener("keydown", function (e) {
          if (e.key === "Escape" && fitModal.style.display !== "none") {
            fitModal.style.display = "none";
          }
        });
      }

      // Tabs accordion
      this.querySelectorAll("[data-tab-toggle]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var target = btn.getAttribute("data-tab-toggle");
          var content = self.querySelector(
            '[data-tab-content="' + target + '"]'
          );
          if (!content) return;
          var isOpen = content.style.display !== "none";
          // Close all tabs
          self.querySelectorAll("[data-tab-content]").forEach(function (c) {
            c.style.display = "none";
          });
          if (!isOpen) {
            content.style.display = "";
          }
        });
      });
    }

    // ── Step management ────────────────────────────────────────

    _initSteps() {
      var self = this;

      // Header toggle — only allow opening completed or current-next step
      this.querySelectorAll("[data-step-toggle]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var n = parseInt(btn.getAttribute("data-step-toggle"), 10);
          var stepEl = self.querySelector('[data-step="' + n + '"]');
          if (!stepEl) return;

          // Allow toggling if step is already confirmed (re-open to review/change)
          // or if it's the next step after the last confirmed one
          var isOpen = stepEl.classList.contains("is-open");
          if (isOpen) {
            stepEl.classList.remove("is-open");
          } else if (self._confirmedSteps.has(n) || n <= self._getNextStep()) {
            stepEl.classList.add("is-open");
          }
        });
      });

      // "Continue" buttons inside steps
      this.querySelectorAll("[data-step-continue]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var n = parseInt(btn.getAttribute("data-step-continue"), 10);
          self._confirmAndAdvance(n);
        });
      });

      // Coating confirmation modal buttons
      var switchBtn = this.querySelector('[data-action="switchToAntiFog"]');
      var keepBtn = this.querySelector('[data-action="keepHardCoat"]');
      var closeBtn = this.querySelector('[data-action="closeCoatingConfirm"]');
      var overlay = this._ref("coatingConfirmOverlay");

      if (switchBtn) {
        switchBtn.addEventListener("click", function () {
          var antiFog = self._findAntiFogVariant();
          if (antiFog) {
            self.lensCoatingSelectedVariant = antiFog;
            self._renderCoatingVariants();
            self._evaluateEyewearSwap();
            self.render();
          }
          self._hideCoatingConfirm();
          self._doConfirmAndAdvance(4);
        });
      }
      if (keepBtn) {
        keepBtn.addEventListener("click", function () {
          self._hideCoatingConfirm();
          self._doConfirmAndAdvance(4);
        });
      }
      if (closeBtn) {
        closeBtn.addEventListener("click", function () {
          self._hideCoatingConfirm();
        });
      }
      if (overlay) {
        overlay.addEventListener("click", function (e) {
          if (e.target === overlay) self._hideCoatingConfirm();
        });
      }
    }

    _renderStickyProgress() {
      var fill = this._ref("stickyProgressFill");
      var label = this._ref("stickyProgressLabel");
      var current = this._ref("stickyCurrentStep");
      if (!fill && !label && !current) return;

      var self = this;
      var visible = 0;
      var done = 0;
      var currentTitle = "";
      this.querySelectorAll("[data-step]").forEach(function (el) {
        if (el.style.display === "none") return;
        visible++;
        var n = parseInt(el.getAttribute("data-step"), 10);
        if (self._confirmedSteps.has(n)) done++;
        if (!currentTitle && el.classList.contains("is-open")) {
          var titleEl = el.querySelector(".pdp-step__title");
          if (titleEl) currentTitle = titleEl.textContent.trim();
        }
      });

      var pct = visible ? Math.round((done / visible) * 100) : 0;
      if (fill) fill.style.width = pct + "%";
      if (label) label.textContent = done + "/" + visible;
      if (current) {
        if (visible === 0) {
          current.textContent = "Build your mask";
        } else if (done === visible) {
          current.textContent = "Ready to dive";
        } else {
          current.textContent = currentTitle || "Build your mask";
        }
      }
    }

    _isStepVisible(n) {
      var el = this.querySelector('[data-step="' + n + '"]');
      return el && el.style.display !== "none";
    }

    _getNextStep() {
      // Returns the next visible step number that should be actionable
      var max = 0;
      this._confirmedSteps.forEach(function (n) {
        if (n > max) max = n;
      });
      var next = max + 1;
      // Skip hidden steps (but don't auto-confirm — visibility can change)
      while (next <= 4 && !this._isStepVisible(next)) {
        next++;
      }
      return next;
    }

    _openStep(n) {
      var el = this.querySelector('[data-step="' + n + '"]');
      if (el) el.classList.add("is-open");
    }

    _closeStep(n) {
      var el = this.querySelector('[data-step="' + n + '"]');
      if (el) el.classList.remove("is-open");
    }

    _completeStep(n) {
      var el = this.querySelector('[data-step="' + n + '"]');
      if (el) el.classList.add("is-completed");
      this._confirmedSteps.add(n);
    }

    _uncompleteStep(n) {
      var el = this.querySelector('[data-step="' + n + '"]');
      if (el) el.classList.remove("is-completed");
      this._confirmedSteps.delete(n);
    }

    _isHardCoatSelected() {
      if (!this.lensCoatingSelectedVariant) return false;
      return this.lensCoatingSelectedVariant.title.toLowerCase().indexOf("hard coat") !== -1;
    }

    _findAntiFogVariant() {
      if (!this.lensCoatingProduct) return null;
      return this.lensCoatingProduct.variants.find(function (v) {
        var t = v.title.toLowerCase();
        return t.indexOf("anti fog") !== -1 || t.indexOf("anti-fog") !== -1;
      }) || null;
    }

    _getCoatingConfirmOverlay() {
      if (this._coatingConfirmOverlay && this._coatingConfirmOverlay.isConnected) {
        return this._coatingConfirmOverlay;
      }
      var overlay = this._ref("coatingConfirmOverlay") ||
        document.querySelector('[data-ref="coatingConfirmOverlay"]');
      this._coatingConfirmOverlay = overlay;
      return overlay;
    }

    _showCoatingConfirm() {
      var overlay = this._getCoatingConfirmOverlay();
      if (!overlay) return;
      if (overlay.parentElement !== document.body) {
        document.body.appendChild(overlay);
      }
      overlay.style.display = "flex";
      document.body.style.overflow = "hidden";
    }

    _hideCoatingConfirm() {
      var overlay = this._getCoatingConfirmOverlay();
      if (!overlay) return;
      overlay.style.display = "none";
      document.body.style.overflow = "";
    }

    // Confirm step without coating check (used after modal decision)
    _doConfirmAndAdvance(current) {
      var self = this;
      for (var i = current + 1; i <= 4; i++) {
        if (this._confirmedSteps.has(i)) {
          this._uncompleteStep(i);
          this._closeStep(i);
        }
      }
      this._completeStep(current);
      this._closeStep(current);
      var next = current + 1;
      while (next <= 4) {
        var nextEl = this.querySelector('[data-step="' + next + '"]');
        if (nextEl && nextEl.style.display !== "none") break;
        next++;
      }
      if (next <= 4) {
        this._openStep(next);
        var nextEl = this.querySelector('[data-step="' + next + '"]');
        if (nextEl) {
          nextEl.classList.remove("is-locked");
          nextEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }
      this._renderStepStates();
    }

    _confirmAndAdvance(current) {
      // Step 4 (coating): warn if Hard Coat selected
      if (current === 4 && this._isHardCoatSelected()) {
        this._showCoatingConfirm();
        return;
      }

      // Invalidate all steps after current (user changed earlier choice)
      var self = this;
      for (var i = current + 1; i <= 4; i++) {
        if (this._confirmedSteps.has(i)) {
          this._uncompleteStep(i);
          this._closeStep(i);
        }
      }

      this._completeStep(current);
      this._closeStep(current);

      // Find next visible step
      var next = current + 1;
      while (next <= 4) {
        var nextEl = this.querySelector('[data-step="' + next + '"]');
        if (nextEl && nextEl.style.display !== "none") break;
        next++;
      }
      if (next <= 4) {
        this._openStep(next);
        // Unlock the next step
        var nextEl = this.querySelector('[data-step="' + next + '"]');
        if (nextEl) {
          nextEl.classList.remove("is-locked");
          nextEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }
      // Re-render to update lock states
      this._renderStepStates();
    }

    _advanceFromStep(current) {
      this._confirmAndAdvance(current);
    }

    _renderStepStates() {
      // Update selection text only for confirmed steps (after Continue click)
      // Step 1: Frame color + size (combined)
      var sel1 = this._ref("step1Selection");
      if (sel1) {
        if (this._confirmedSteps.has(1)) {
          var parts = [];
          var color = this._getFrameColor();
          if (color) parts.push(color);
          if (this.selectedVariant && !(this.product && this.product.has_only_default_variant)) {
            parts.push(this.selectedVariant.title);
          }
          sel1.textContent = parts.join(" · ");
        } else {
          sel1.textContent = "";
        }
      }

      // Step 2: Lens technology (eyewear)
      var sel2 = this._ref("step2Selection");
      if (sel2) sel2.textContent = this._confirmedSteps.has(2) ? (this.activeEyewear || "") : "";

      // Step 3: Lens design + material
      var sel3 = this._ref("step3Selection");
      if (sel3) {
        sel3.textContent = this._confirmedSteps.has(3) && this.rxLensSelectedVariant
          ? this.rxLensSelectedVariant.title
          : "";
      }

      // Step 4: Coating
      var sel4 = this._ref("step4Selection");
      if (sel4) {
        sel4.textContent = this._confirmedSteps.has(4) && this.lensCoatingSelectedVariant
          ? this.lensCoatingSelectedVariant.title
          : "";
      }

      // Re-apply is-completed and is-locked classes
      var self = this;
      var nextStep = this._getNextStep();
      this.querySelectorAll("[data-step]").forEach(function (el) {
        var n = parseInt(el.getAttribute("data-step"), 10);
        // Skip hidden steps (display:none via data-show)
        if (el.style.display === "none") {
          // Hidden steps must not stay open — otherwise their is-open state
          // blocks the auto-open logic below from advancing focus.
          el.classList.remove("is-open");
          return;
        }

        if (self._confirmedSteps.has(n)) {
          el.classList.add("is-completed");
          el.classList.remove("is-locked");
        } else if (n > nextStep) {
          el.classList.add("is-locked");
          el.classList.remove("is-completed");
        } else {
          el.classList.remove("is-locked");
          el.classList.remove("is-completed");
        }
      });

      // Ensure at least one visible non-locked, non-completed step is open.
      // Triggers when an earlier step becomes hidden (e.g. only one frame
      // color → step 1 has nothing to show) — focus jumps to the next one.
      var visibleSteps = Array.prototype.filter.call(
        this.querySelectorAll("[data-step]"),
        function (el) { return el.style.display !== "none"; }
      );
      var anyOpen = visibleSteps.some(function (el) {
        return el.classList.contains("is-open");
      });
      if (!anyOpen) {
        // Open the first non-confirmed, currently-actionable step.
        // Do NOT fall back to step 0 when everything is confirmed —
        // an empty (all-done) state is correct after the final Continue.
        var nextOpenable = visibleSteps.find(function (el) {
          var n = parseInt(el.getAttribute("data-step"), 10);
          return !self._confirmedSteps.has(n) && n <= nextStep;
        });
        if (nextOpenable) nextOpenable.classList.add("is-open");
      }

      // Summary price
      var summaryPrice = this._ref("summaryPrice");
      if (summaryPrice) {
        summaryPrice.textContent = window.currency
          ? window.currency(this._getPrice())
          : this._getPrice();
      }
      var summaryComp = this._ref("summaryComparePrice");
      var cap = this._getCompareAtPrice();
      if (summaryComp) {
        if (cap) {
          summaryComp.style.display = "";
          summaryComp.textContent = window.currency
            ? window.currency(cap.price)
            : cap.price;
        } else {
          summaryComp.style.display = "none";
        }
      }

      this._renderStickyProgress();
    }

    // ── Business logic methods ─────────────────────────────────

    _changeProductByProductType({ productType, product }) {
      var self = this;
      switch (productType) {
        case "frame_product": {
          // Keep the size the customer already picked when the new colour has
          // it in stock, otherwise fall back to the first size that is actually
          // available - variants[0] can be sold out, which used to leave the
          // picker with nothing selected and the order sold out at checkout.
          var previousSize = this.selectedVariant && this.selectedVariant.title;
          this.product = product;
          this.selectedVariant =
            this._availableVariantByTitle(product, previousSize) ||
            this._firstAvailableVariant(product);
          this.productOptions = product.productOptions;
          this._changeUrl({
            product: this.product,
            selectedVariant: this.selectedVariant,
          });
          this._setProductImagesByAlt();
          this.render();
          this._initSliders();
          requestAnimationFrame(function () {
            if (self._mobileSlider) self._mobileSlider.slideTo(0);
          });
          return;
        }
        case "lens_product": {
          // Capture ALL previous option values (including Color) so swap-clones
          // can re-select the exact same variant. Filtering happens at match
          // time inside _setRxLensProductInstance.
          var allPrevOptions = (this.rxLensSelectedVariant
            ? this.rxLensSelectedVariant.options.slice()
            : []);
          var nonColorPrevOptions = [];
          var self2 = this;
          this.rxLensProductOptions.forEach(function (o) {
            if (o.name === "Color") return;
            nonColorPrevOptions.push(
              self2.rxLensSelectedVariant.options[o.position - 1]
            );
          });

          this._setRxLensProductInstance({
            product: product,
            prevOptions: nonColorPrevOptions,
            allPrevOptions: allPrevOptions,
          });
          this._setProductImagesByAlt();
          this.render();
          this._initSliders();
          requestAnimationFrame(function () {
            if (self._mobileSlider) self._mobileSlider.slideTo(0);
          });
          return;
        }
      }
    }

    _changeVariant(optionObj, optionValue) {
      MO.helpers.selectOption(this, optionObj, optionValue);
      this._setProductImagesByAlt();
      this.render();
      this._initSliders();
    }

    _initSliders() {
      var self = this;

      // Destroy old sliders
      if (this._mobileSlider) this._mobileSlider.destroy();
      if (this._externalSlider) this._externalSlider.destroy();

      // Mobile slider
      var mobileContainer = this.querySelector(
        ".main-slider--" + this.sectionId
      );
      if (mobileContainer) {
        this._mobileSlider = new MO.Slider(mobileContainer, {
          slidesPerView: 1.2,
          spaceBetween: 8,
          pagination: true,
        });
      }

      // External gallery slider
      var extContainer = this.querySelector(
        ".external-gallery-slider--" + this.sectionId
      );
      if (extContainer) {
        this._externalSlider = new MO.Slider(extContainer, {
          slidesPerView: 2,
          slidesPerGroup: 2,
          spaceBetween: 8,
          pagination: true,
        });
      }
    }

    _setRxLensProductInstance({ product, prevOptions, allPrevOptions }) {
      this.rxLensProduct = product;

      // 1) Try matching ALL previous options (including Color) — exact match
      //    so swap-clones with the same structure preserve every selection.
      var match = null;
      if (allPrevOptions && allPrevOptions.length) {
        match = product.variants.find(function (variant) {
          return allPrevOptions.every(function (option) {
            return variant.options.includes(option);
          });
        });
      }
      // 2) Fallback to non-Color options only — handles switches between
      //    products with different option structures (Photochromic ↔ Clear).
      if (!match) {
        match = product.variants.find(function (variant) {
          return prevOptions.every(function (option) {
            return variant.options.includes(option);
          });
        });
      }
      this.rxLensSelectedVariant = match || product.variants[0];
      this.rxLensProductOptions = product.productOptions;
    }

    _firstAvailableVariant(product) {
      if (!product || !product.variants || !product.variants.length) return null;
      var available = product.variants.find(function (v) {
        return v.available;
      });
      return available || product.variants[0];
    }

    _availableVariantByTitle(product, title) {
      if (!title || !product || !product.variants) return null;
      return (
        product.variants.find(function (v) {
          return v.available && v.title === title;
        }) || null
      );
    }

    _setInstance({ product, productOptions, selectedVariant }) {
      this.product = product;
      this.productOptions = productOptions;
      this.selectedVariant = selectedVariant;
      this._changeUrl({ product, selectedVariant });
    }

    _setDefaultActiveTags(productTags) {
      if (!productTags) return;
      var self = this;
      productTags.forEach(function (tag) {
        var key = tag.split("_")[0];
        if (key in self.activeTags) {
          self.activeTags[key] = tag;
        }
      });
    }

    _setProductImagesByAlt() {
      if (!this.product) return;
      var media = this.product.media
        ? this.product.media.filter(function (item) {
            return item.media_type === "image";
          })
        : [];

      if (!this.rxLensProduct) {
        this.images = media;
        return;
      }

      var eyewear = this.rxLensProduct.tags
        ? this.rxLensProduct.tags.find(function (tag) {
            return tag.includes("eyewear_");
          })
        : null;

      if (!eyewear) {
        this.images = media;
        return;
      }

      switch (eyewear) {
        case "eyewear_Safety-Solid Tint-Lenses":
        case "eyewear_Safety-Polarized-Lenses":
        case "eyewear_Safety-Photochromic-Lenses":
        case "eyewear_Safety-Transitions-Lenses":
        case "eyewear_Safety-Drivewear-Lenses": {
          var lensColorOption = this.rxLensProduct.productOptions.find(
            function (option) {
              return option.name.toLowerCase() === "color";
            }
          );
          if (!lensColorOption || !this.rxLensSelectedVariant) {
            this.images = media;
            return;
          }
          var lensColorOptionValue =
            this.rxLensSelectedVariant.options[lensColorOption.position - 1];
          var targetAlt = eyewear + "~" + lensColorOptionValue;
          var filtered = media.filter(function (img) {
            return img.alt ? img.alt === targetAlt : false;
          });
          this.images = filtered.length > 0 ? filtered : media;
          return;
        }
        default: {
          var targetAlt2 = eyewear;
          var filtered2 = media.filter(function (img) {
            return img.alt ? img.alt === targetAlt2 : false;
          });
          this.images = filtered2.length > 0 ? filtered2 : media;
          return;
        }
      }
    }

    _changeUrl({ product, selectedVariant }) {
      var url = new URL(window.location.href);
      var params = new URLSearchParams(url.search);
      url.pathname = "/products/" + product.handle;
      params.set("variant", selectedVariant.id);
      var newUrl =
        url.origin + url.pathname + "?" + params.toString() + url.hash;
      history.replaceState(null, "", newUrl);
    }

    _setCssVariables() {
      var wrapper = document.querySelector(".js-frame-selector-wrapper");
      if (!wrapper) return;
      var height = wrapper.getBoundingClientRect().height;
      document.documentElement.style.setProperty(
        "--frame-selector-wrapper-height",
        height + "px"
      );
    }

    _buildFramesByGroup(shopProducts) {
      var framesByGroupMap = new Map();
      Object.values(shopProducts).forEach(function (product) {
        var frameTag = product.tags.find(function (tag) {
          return tag.includes("frame_");
        });
        if (frameTag) {
          var frameGroup = frameTag.split("~")[0].split("_")[1];
          if (!framesByGroupMap.has(frameGroup)) {
            framesByGroupMap.set(frameGroup, product);
          }
        }
      });

      this.framesByGroup = Array.from(framesByGroupMap).map(function (entry) {
        var key = entry[0];
        var value = entry[1];
        return {
          frameGroup: key,
          defaultFrameGroupProduct: value,
          image: value.media ? value.media[0]?.src : null,
          allFrames: Object.values(shopProducts).filter(function (product) {
            var frameTag = product.tags.find(function (tag) {
              return tag.includes("frame_");
            });
            if (frameTag) {
              var fg = frameTag.split("~")[0].split("_")[1];
              return key === fg;
            }
            return false;
          }),
        };
      });
    }

    _onScroll() {
      if (window.outerWidth < 1024) return;
      var scrollY = window.scrollY;
      if (scrollY > 350) {
        this.classList.add("lg:!tw-translate-y-0");
      } else {
        this.classList.remove("lg:!tw-translate-y-0");
      }
    }

    async _getDataFromPage(url) {
      var resText = await fetch(url);
      var html = await resText.text();
      var parser = new DOMParser();
      var htmlDoc = parser.parseFromString(html, "text/html");
      var el = htmlDoc.getElementById("all-products");
      if (!el) return {};
      return JSON.parse(el.textContent);
    }

    // ── Cart ──────────────────────────────────────────────────

    async addToCartFrame() {
      try {
        this.setAttribute("aria-busy", "true");
        var bundleHash = new Date().getTime();

        var lensProperties = {
          _bundleHash: bundleHash,
          temple1: this.temple1,
          temple2: this.temple2,
        };
        if (this.measurementId) {
          lensProperties.measurementId = this.measurementId;
        }

        await MO.api.addItems([
          {
            id: this.selectedVariant?.id,
            quantity: 1,
            properties: { _bundleHash: bundleHash },
          },
          {
            id: this.noLensVariantId,
            quantity: 1,
            properties: lensProperties,
          },
        ]);
        window.location.href = "/cart";
      } catch (e) {
        this.setAttribute("aria-busy", "false");
        console.error(e);
      }
    }

    // ── Togglers ──────────────────────────────────────────────

    togglePrescriptionPopup() {
      this.isOpenPrescriptionPopup = !this.isOpenPrescriptionPopup;
      if (this.isOpenPrescriptionPopup) {
        window.scrollTo({ top: 0 });
        document.documentElement.classList.add("prescription-popup-opened");
        MO.scrollLock.enable();
      } else {
        document.documentElement.classList.remove("prescription-popup-opened");
        MO.scrollLock.disable();
      }
      this.render();
    }

    _onRxTogglePopup() {
      this.togglePrescriptionPopup();
    }

    addToCartOrPrescription(e, el) {
      if (this.orderType === "Prescription") {
        this.togglePrescriptionPopup();
      } else {
        this.addToCartFrame();
      }
    }

    changeActiveEyewear(eyewear) {
      this.activeEyewear = eyewear;
    }

    openIframeTemple(handler) {
      this.handler = handler;
      var html =
        '<style>' +
        '#temple-iframe .temple-container { display: grid; grid-template-columns: 380px 1fr; gap: 40px; align-items: center; padding: 40px; max-width: 100%; height: 100%; }' +
        '#temple-iframe .instructions-section { position: sticky; top: 20px; background: hsl(0 0% 98%); border: 1px solid hsl(0 0% 90%); border-radius: 6px; padding: 24px; }' +
        '#temple-iframe .instructions-section h2 { font-size: 16px; font-weight: 600; color: hsl(0 0% 9%); margin-bottom: 16px; }' +
        '#temple-iframe .instructions-grid { display: flex; flex-direction: column; gap: 16px; }' +
        '#temple-iframe .instruction-item h3 { font-size: 14px; font-weight: 600; color: hsl(0 0% 9%); margin-bottom: 6px; }' +
        '#temple-iframe .instruction-item ul { margin: 0; padding-left: 20px; list-style: disc; }' +
        '#temple-iframe .instruction-item li { font-size: 13px; color: hsl(0 0% 25%); line-height: 1.7; margin-bottom: 4px; }' +
        '#temple-iframe .instruction-item li strong { color: hsl(0 0% 9%); font-weight: 600; }' +
        '#temple-iframe .back-button { width: 100%; background: white; color: hsl(0 0% 9%); padding: 10px 16px; border: 1px solid hsl(0 0% 90%); border-radius: 6px; font-size: 14px; font-weight: 500; cursor: pointer; transition: background 0.2s ease; margin-top: 16px; }' +
        '#temple-iframe .back-button:hover { background: hsl(0 0% 98%); }' +
        '#temple-iframe .iframe-wrapper { position: relative; width: 100%; height: 85vh; max-height: 900px; border: 1px solid hsl(0 0% 90%); border-radius: 6px; overflow: hidden; background: hsl(0 0% 98%); }' +
        '#temple-iframe .iframe-wrapper iframe { width: 100%; height: 100%; border: none; }' +
        '#temple-iframe .instructions-wrapper { display: flex; flex-direction: column; }' +
        '@media (max-width: 1024px) { #temple-iframe .temple-container { grid-template-columns: 1fr; padding: 20px; overflow-y: scroll; } #temple-iframe .instructions-section { position: static; } #temple-iframe .instructions-wrapper { order: 2; } #temple-iframe .iframe-wrapper { height: 70vh; max-height: 600px; order: 1; } }' +
        '</style>' +
        '<div id="temple-iframe" class="tw-fixed tw-z-full tw-inset-0 tw-w-full tw-h-full tw-backdrop-blur-6px tw-bg-white/90">' +
        '<div class="temple-container">' +
        '<div class="instructions-wrapper">' +
        '<div class="instructions-section">' +
        '<h2>Frame Measurement Instructions</h2>' +
        '<div class="instructions-grid">' +
        '<div class="instruction-item"><h3>Positioning</h3><ul><li><strong>Distance:</strong> Position yourself 40-50 cm (16-20 inches) from the camera</li><li><strong>Angle:</strong> Turn your head to show a full side profile, perpendicular to the camera</li><li><strong>Height:</strong> Keep the camera at eye level</li></ul></div>' +
        '<div class="instruction-item"><h3>Requirements</h3><ul><li>Remove glasses if you\'re wearing any</li><li>Show your ear clearly – make sure it\'s fully visible and not covered by hair or clothing</li><li>Tie back hair if necessary to expose the entire ear</li><li>Face forward – keep your head level, don\'t tilt up or down</li></ul></div>' +
        '<div class="instruction-item"><h3>Environment</h3><ul><li>Good lighting – ensure your face is well-lit with no harsh shadows</li><li>Avoid backlighting – don\'t sit with a window or bright light behind you</li></ul></div>' +
        '<div class="instruction-item"><h3>During Measurement</h3><ul><li>Stay still and maintain your position</li><li>Keep a neutral expression</li></ul></div>' +
        '</div></div>' +
        '<button class="back-button" onclick="document.querySelector(\'#temple-iframe\').remove()">← Close</button>' +
        '</div>' +
        '<div class="iframe-wrapper"><iframe src="' + TEMPLE_IFRAME_URL + '/?primaryColor=FFF&textColor=000&lineColor=FFF&borderRadius=3px" width="100%" height="100%" frameborder="0" allow="camera"></iframe></div>' +
        '</div></div>';

      document.documentElement.insertAdjacentHTML("beforeend", html);
    }

    closeIframeTemple() {
      var el = document.querySelector("#temple-iframe");
      if (el) el.remove();
      this.handler = null;
    }

    // ── Getters (computed) ────────────────────────────────────

    _getFrameTag() {
      if (!this.product || !this.product.tags) return "";
      var tag = this.product.tags.find(function (t) {
        return t.includes("frame_");
      });
      return tag || "";
    }

    _getFrameGroup() {
      var tag = this._getFrameTag();
      return tag ? tag.split("~")[0].split("_")[1] : "";
    }

    _getFrameColor() {
      var tag = this._getFrameTag();
      return tag ? tag.split("~")[1] || "" : "";
    }

    _getFrameTagByProduct(product) {
      if (!product || !product.tags) return "";
      var tag = product.tags.find(function (t) {
        return t.includes("frame_");
      });
      return tag || "";
    }

    _getFrameColorByProduct(product) {
      var tag = this._getFrameTagByProduct(product);
      return tag ? tag.split("~")[1] || "" : "";
    }

    _getPrice() {
      if (!this.selectedVariant) return 0;
      if (this.orderType === "Prescription" && this.rxLensSelectedVariant) {
        var base =
          this.selectedVariant.price + this.rxLensSelectedVariant.price;
        if (this.isAddedCoatingProduct && this.lensCoatingSelectedVariant) {
          return base + this.lensCoatingSelectedVariant.price;
        }
        return base;
      }
      return this.selectedVariant.price;
    }

    _getCompareAtPrice() {
      if (!this.selectedVariant || !this.rxLensSelectedVariant) return null;

      var originalPrice = 0;
      var newPrice = 0;

      if (
        this.selectedVariant.compare_at_price &&
        this.selectedVariant.compare_at_price > this.selectedVariant.price
      ) {
        originalPrice += this.selectedVariant.compare_at_price;
        newPrice += this.selectedVariant.price;
      } else {
        originalPrice += this.selectedVariant.price;
        newPrice += this.selectedVariant.price;
      }

      if (
        this.rxLensSelectedVariant.compare_at_price &&
        this.rxLensSelectedVariant.compare_at_price >
          this.rxLensSelectedVariant.price
      ) {
        originalPrice += this.rxLensSelectedVariant.compare_at_price;
        newPrice += this.rxLensSelectedVariant.price;
      } else {
        originalPrice += this.rxLensSelectedVariant.price;
        newPrice += this.rxLensSelectedVariant.price;
      }

      if (originalPrice > newPrice) {
        var discountAmount = originalPrice - newPrice;
        var discountPercentage = (
          (discountAmount / originalPrice) *
          100
        ).toFixed(0);

        var res = {
          price: originalPrice,
          percentage: discountPercentage + "%",
        };

        if (this.isAddedCoatingProduct && this.lensCoatingSelectedVariant) {
          res.price = res.price + this.lensCoatingSelectedVariant.price;
        }
        return res;
      }

      return null;
    }

    // ── Push context to rx-form WC ────────────────────────────
    _pushRxFormContext() {
      var rxEl = this.querySelector("rx-form-safety");
      if (rxEl && typeof rxEl.setContext === "function") {
        rxEl.setContext({
          rxLensSelectedVariant: this.rxLensSelectedVariant,
          selectedVariant: this.selectedVariant,
          rxLensProduct: this.rxLensProduct,
          frameTag: this._getFrameTag(),
          frameName: this._getFrameGroup(),
          isAddedCoatingProduct: this.isAddedCoatingProduct,
          lensCoatingSelectedVariant: this.lensCoatingSelectedVariant,
          customSize: this.customSize,
          temple1: this.temple1,
          temple2: this.temple2,
          measurementId: this.measurementId,
          activeEyewear: this.activeEyewear,
        });
      }
    }
  }

  customElements.define("hero-safety-frame", HeroSafetyFrame);
})();
